import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import usePageTitle from "@/hooks/usePageTitle";
import ErrorBoundary from "@/components/ErrorBoundary";
import TableSkeleton from "@/components/Skeleton/TableSkeleton";
import Table, { TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { withGlobalEmptyStates } from "@/components/Table/utils";
import { PARTICIPATION_HISTORY_QUERY } from "../../queries";
import type { ParticipationHistoryData, ParticipationHistoryRow, ParticipationHistoryTableRow } from "../types";
import { getParticipationHistoryColumns } from "./_components/TableColumns";

function formatTableData(participations: ParticipationHistoryRow[]): ParticipationHistoryTableRow[] {
  return participations.map((participation) => ({
    ...participation,
    id: `${participation.eventId}-${participation.registrationSerialNumber}`
  }));
}

export default function ParticipationHistoryPage() {
  usePageTitle("/people/events/participation-history", "Participation History");
  const { pgaId } = useParams();

  const { data, loading, error } = useQuery<ParticipationHistoryData>(PARTICIPATION_HISTORY_QUERY, {
    variables: { pgaId: pgaId ?? "" },
    fetchPolicy: "cache-first",
    skip: !pgaId
  });

  const participations = useMemo(() => data?.person?.historicalTournamentParticipations ?? [], [data]);
  const tableData = useMemo(() => formatTableData(participations), [participations]);
  const columns = useMemo(() => withGlobalEmptyStates(getParticipationHistoryColumns()), []);

  if (error) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Participation History"
        message={error.message}
        className="mt-12"
      />
    );
  }

  if (loading) {
    return <TableSkeleton columns={5} rows={4} />;
  }

  return (
    <div className="w-full">
      <div className="pt-12" />
      <Table
        data={tableData}
        loading={false}
        columns={columns}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Participation History"
            caption="This person has no historical tournament participations."
            iconName="event_busy"
          />
        )}
        hasActiveFilters={false}
        renderFooter={() => <TableFooter totalRowCount={tableData.length} />}
      />
    </div>
  );
}
